import { z } from 'zod';

export const FISCAL_OPERATION_KINDS = [
  'SALE',
  'PURCHASE',
  'RETURN',
  'TRANSFER',
  'REMITTANCE',
  'SERVICE',
  'WARRANTY',
  'BONUS',
  'INTERNAL_CONSUMPTION',
  'ADJUSTMENT',
] as const;

export const FISCAL_DOCUMENT_KINDS = ['NFE', 'NFCE', 'NFSE', 'CTE', 'MDFE', 'EXTERNAL'] as const;

export const TAX_RULE_SET_STATUSES = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED'] as const;
export const FISCAL_ITEM_TYPES = ['PRODUCT', 'SERVICE'] as const;
export const RULE_CONDITION_OPERATORS = [
  'EQ',
  'NE',
  'GT',
  'LT',
  'GTE',
  'LTE',
  'IN',
  'CONTAINS',
] as const;

export const TAX_OUTPUT_ROUTE_KINDS = [
  'icms',
  'icms-st',
  'difal',
  'pis-cofins',
  'ipi',
  'iss',
  'federal-retention',
  'ibs-cbs',
] as const;

export const PlaceTaxRuleSetIdParamsSchema = z.looseObject({
  placeId: z.uuid(),
  ruleSetId: z.uuid(),
});

export const PlaceTaxRuleIdParamsSchema = z.looseObject({
  placeId: z.uuid(),
  ruleId: z.uuid(),
});

export const PlaceTaxRuleOutputParamsSchema = z.looseObject({
  placeId: z.uuid(),
  ruleId: z.uuid(),
  tax: z.enum(TAX_OUTPUT_ROUTE_KINDS),
});

export const CreatePlaceTaxRuleSetBodySchema = z.looseObject({
  name: z.string().min(1),
  validFrom: z.iso.date(),
});

export const CreatePlaceTaxRuleSetResponseSchema = z.looseObject({
  ruleSetId: z.uuid(),
  version: z.number().int(),
  status: z.literal('DRAFT'),
});

export const DuplicatePlaceTaxRuleSetBodySchema = z.looseObject({
  name: z.string().min(1).optional(),
  validFrom: z.iso.date().optional(),
});

export const DuplicatePlaceTaxRuleSetResponseSchema = z.looseObject({
  ruleSetId: z.uuid(),
  version: z.number().int(),
  status: z.literal('DRAFT'),
  rulesCloned: z.number().int().nonnegative(),
});

export const PlaceTaxRuleSetListItemSchema = z.looseObject({
  ruleSetId: z.uuid(),
  placeId: z.uuid(),
  version: z.number().int(),
  name: z.string(),
  status: z.enum(TAX_RULE_SET_STATUSES),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable(),
  activatedAt: z.iso.datetime().nullable(),
  rulesCount: z.number().int().nonnegative(),
  provisioningKind: z.enum(['CORRIDOR_BOOTSTRAP', 'MANUAL']).optional(),
});

export const PlaceTaxRuleListItemSchema = z.looseObject({
  ruleId: z.uuid(),
  name: z.string(),
  priority: z.number().int(),
  stopProcessing: z.boolean(),
  documentKind: z.enum(FISCAL_DOCUMENT_KINDS).nullable(),
  itemType: z.enum(FISCAL_ITEM_TYPES).nullable(),
  operationKind: z.enum(FISCAL_OPERATION_KINDS).nullable(),
  taxRegime: z.enum(['SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL', 'MEI']).nullable(),
  cfopCode: z.string().length(4).nullable(),
});

export const ListPlaceTaxRulesByRuleSetResponseSchema = z.looseObject({
  ruleSetId: z.uuid(),
  status: z.enum(TAX_RULE_SET_STATUSES),
  items: z.array(PlaceTaxRuleListItemSchema),
});

export const ListPlaceTaxRuleSetsResponseSchema = z.looseObject({
  items: z.array(PlaceTaxRuleSetListItemSchema),
});

export const ActivatePlaceTaxRuleSetResponseSchema = z.looseObject({
  ruleSetId: z.uuid(),
  status: z.literal('ACTIVE'),
  activatedAt: z.iso.datetime(),
});

export const ResolveActiveRuleSetQuerySchema = z.looseObject({
  competenceDate: z.iso.date(),
});

export const UpsertPlaceTaxRuleBodySchema = z.looseObject({
  ruleId: z.uuid().optional(),
  name: z.string().min(1),
  priority: z.number().int().optional(),
  stopProcessing: z.boolean().optional(),
  taxRegime: z
    .enum(['SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL', 'MEI'])
    .nullable()
    .optional(),
  documentKind: z.enum(FISCAL_DOCUMENT_KINDS).nullable().optional(),
  itemType: z.enum(FISCAL_ITEM_TYPES).nullable().optional(),
  operationKind: z.enum(FISCAL_OPERATION_KINDS).nullable().optional(),
  originStateCode: z.string().length(2).nullable().optional(),
  destinationStateCode: z.string().length(2).nullable().optional(),
  isInterstate: z.boolean().nullable().optional(),
  isFinalConsumer: z.boolean().nullable().optional(),
  ncmCode: z.string().max(10).nullable().optional(),
  cestCode: z.string().max(10).nullable().optional(),
  cfopCode: z.string().length(4).nullable().optional(),
  lc116Code: z.string().max(10).nullable().optional(),
  minAmountCents: z.string().regex(/^\d+$/).nullable().optional(),
  maxAmountCents: z.string().regex(/^\d+$/).nullable().optional(),
});

export const UpsertPlaceTaxRuleResponseSchema = z.looseObject({
  ruleId: z.uuid(),
  ruleSetId: z.uuid(),
  name: z.string(),
  priority: z.number().int(),
});

export const RuleConditionItemSchema = z.looseObject({
  fieldName: z.string().min(1),
  operator: z.enum(RULE_CONDITION_OPERATORS),
  valueText: z.string().nullable().optional(),
  valueJson: z.unknown().nullable().optional(),
});

export const UpsertRuleConditionBodySchema = z.looseObject({
  conditions: z.array(RuleConditionItemSchema),
});

export const UpsertRuleConditionResponseSchema = z.looseObject({
  ruleId: z.uuid(),
  conditionCount: z.number().int(),
});

export const UpsertIcmsRuleOutputBodySchema = z.looseObject({
  cfopCode: z.string().length(4).nullable().optional(),
  taxBenefitCodeId: z.uuid().nullable().optional(),
  fiscalObservation: z.string().nullable().optional(),
  icmsCstCode: z.string().max(3).nullable().optional(),
  icmsCsosnCode: z.string().max(4).nullable().optional(),
  rateBp: z.number().int().nullable().optional(),
  reductionRateBp: z.number().int().nullable().optional(),
  desonerationReason: z.string().max(10).nullable().optional(),
});

export const UpsertPisCofinsRuleOutputBodySchema = z.looseObject({
  cfopCode: z.string().length(4).nullable().optional(),
  pisCstCode: z.string().max(2).nullable().optional(),
  cofinsCstCode: z.string().max(2).nullable().optional(),
  pisRateBp: z.number().int().nullable().optional(),
  cofinsRateBp: z.number().int().nullable().optional(),
  creditAllowed: z.boolean().optional(),
});

export const UpsertIcmsStRuleOutputBodySchema = z.looseObject({
  cfopCode: z.string().length(4).nullable().optional(),
  mvaRateBp: z.number().int().nullable().optional(),
  rateStBp: z.number().int().nullable().optional(),
  reductionStRateBp: z.number().int().nullable().optional(),
  cestRequired: z.boolean().optional(),
});

export const UpsertDifalRuleOutputBodySchema = z.looseObject({
  cfopCode: z.string().length(4).nullable().optional(),
  interstateRateBp: z.number().int().nullable().optional(),
  internalDestinationRateBp: z.number().int().nullable().optional(),
  destinationShareRateBp: z.number().int().nullable().optional(),
  fcpRateBp: z.number().int().nullable().optional(),
});

export const UpsertIpiRuleOutputBodySchema = z.looseObject({
  cfopCode: z.string().length(4).nullable().optional(),
  ipiCstCode: z.string().max(2).nullable().optional(),
  ipiRateBp: z.number().int().nullable().optional(),
  ipiEnquadramentoCode: z.string().max(10).nullable().optional(),
  includeInIcmsBase: z.boolean().optional(),
});

export const UpsertIssRuleOutputBodySchema = z.looseObject({
  cfopCode: z.string().length(4).nullable().optional(),
  issRateBp: z.number().int().nullable().optional(),
  issWithheld: z.boolean().optional(),
  issDueCityIbgeCode: z.string().max(7).nullable().optional(),
  serviceListItemCode: z.string().max(10).nullable().optional(),
});

export const UpsertFederalRetentionRuleOutputBodySchema = z.looseObject({
  cfopCode: z.string().length(4).nullable().optional(),
  irrfRateBp: z.number().int().nullable().optional(),
  csllRateBp: z.number().int().nullable().optional(),
  inssRateBp: z.number().int().nullable().optional(),
  pisRetentionRateBp: z.number().int().nullable().optional(),
  cofinsRetentionRateBp: z.number().int().nullable().optional(),
  minimumRetentionCents: z.string().regex(/^\d+$/).nullable().optional(),
  currency: z.string().length(3).optional(),
});

export const UpsertIbsCbsRuleOutputBodySchema = z.looseObject({
  cfopCode: z.string().length(4).nullable().optional(),
  cstCode: z.string().length(3),
  classTaxationCode: z.string().min(1).max(20),
  ibsStateRateBp: z.number().int().nullable().optional(),
  ibsCityRateBp: z.number().int().nullable().optional(),
  cbsRateBp: z.number().int().nullable().optional(),
  reductionRateBp: z.number().int().nullable().optional(),
  transitionYear: z.number().int().nullable().optional(),
});

export const UpsertRuleOutputResponseSchema = z.looseObject({
  ruleId: z.uuid(),
  outputId: z.uuid(),
  taxKind: z.string(),
});

export const RULE_CONDITION_FIELD_NAMES = [
  'customer_type',
  'customer_state_code',
  'product_origin_code',
  'service_city_ibge_code',
  'vehicle_category',
  'payment_method',
] as const;

export const PlaceTaxRuleConditionReadSchema = z.looseObject({
  fieldName: z.enum(RULE_CONDITION_FIELD_NAMES),
  operator: z.enum(RULE_CONDITION_OPERATORS),
  valueText: z.string().nullable(),
  valueJson: z.unknown().nullable(),
});

export const PlaceTaxRuleOutputReadSchema = z.looseObject({
  cfopCode: z.string().length(4).nullable(),
  fiscalObservation: z.string().nullable(),
  icmsEnabled: z.boolean(),
  icmsCstCode: z.string().max(3).nullable(),
  icmsCsosnCode: z.string().max(4).nullable(),
  icmsRateBp: z.number().int().nullable(),
  icmsReductionRateBp: z.number().int().nullable(),
  pisCofinsEnabled: z.boolean(),
  pisCstCode: z.string().max(2).nullable(),
  cofinsCstCode: z.string().max(2).nullable(),
  pisRateBp: z.number().int().nullable(),
  cofinsRateBp: z.number().int().nullable(),
  creditAllowed: z.boolean(),
  issEnabled: z.boolean(),
  issRateBp: z.number().int().nullable(),
  issWithheld: z.boolean(),
  issDueCityIbgeCode: z.string().max(7).nullable(),
  serviceListItemCode: z.string().max(10).nullable(),
  ibsCbsEnabled: z.boolean(),
  ibsCstCode: z.string().length(3).nullable(),
  ibsClassTaxationCode: z.string().max(20).nullable(),
  ibsStateRateBp: z.number().int().nullable(),
  ibsCityRateBp: z.number().int().nullable(),
  cbsRateBp: z.number().int().nullable(),
  ibsReductionRateBp: z.number().int().nullable(),
  ibsTransitionYear: z.number().int().nullable(),
});

export const GetPlaceTaxRuleResponseSchema = z.looseObject({
  ruleId: z.uuid(),
  ruleSetId: z.uuid(),
  name: z.string(),
  priority: z.number().int(),
  stopProcessing: z.boolean(),
  taxRegime: z.enum(['SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL', 'MEI']).nullable(),
  documentKind: z.enum(FISCAL_DOCUMENT_KINDS).nullable(),
  itemType: z.enum(FISCAL_ITEM_TYPES).nullable(),
  operationKind: z.enum(FISCAL_OPERATION_KINDS).nullable(),
  originStateCode: z.string().length(2).nullable(),
  destinationStateCode: z.string().length(2).nullable(),
  isInterstate: z.boolean().nullable(),
  isFinalConsumer: z.boolean().nullable(),
  ncmCode: z.string().max(10).nullable(),
  cestCode: z.string().max(10).nullable(),
  cfopCode: z.string().length(4).nullable(),
  lc116Code: z.string().max(10).nullable(),
  minAmountCents: z.string().regex(/^\d+$/).nullable(),
  maxAmountCents: z.string().regex(/^\d+$/).nullable(),
  conditions: z.array(PlaceTaxRuleConditionReadSchema),
  output: PlaceTaxRuleOutputReadSchema.nullable(),
});

export const SoftDeletePlaceTaxRuleResponseSchema = z.looseObject({
  ruleId: z.uuid(),
  deleted: z.literal(true),
});

export type CreatePlaceTaxRuleSetBody = z.infer<typeof CreatePlaceTaxRuleSetBodySchema>;
export type DuplicatePlaceTaxRuleSetBody = z.infer<typeof DuplicatePlaceTaxRuleSetBodySchema>;
export type UpsertPlaceTaxRuleBody = z.infer<typeof UpsertPlaceTaxRuleBodySchema>;
export type UpsertRuleConditionBody = z.infer<typeof UpsertRuleConditionBodySchema>;
export type UpsertIcmsRuleOutputBody = z.infer<typeof UpsertIcmsRuleOutputBodySchema>;
export type UpsertPisCofinsRuleOutputBody = z.infer<typeof UpsertPisCofinsRuleOutputBodySchema>;
export type UpsertIcmsStRuleOutputBody = z.infer<typeof UpsertIcmsStRuleOutputBodySchema>;
export type UpsertDifalRuleOutputBody = z.infer<typeof UpsertDifalRuleOutputBodySchema>;
export type UpsertIpiRuleOutputBody = z.infer<typeof UpsertIpiRuleOutputBodySchema>;
export type UpsertIssRuleOutputBody = z.infer<typeof UpsertIssRuleOutputBodySchema>;
export type UpsertFederalRetentionRuleOutputBody = z.infer<
  typeof UpsertFederalRetentionRuleOutputBodySchema
>;
export type UpsertIbsCbsRuleOutputBody = z.infer<typeof UpsertIbsCbsRuleOutputBodySchema>;
